"use client"

import type { SearchClient } from "instantsearch.js"
import Image from "next/image"
import { useParams, useRouter } from "next/navigation"
import { useCallback, useEffect, useRef, useState } from "react"
import { Configure, InstantSearch, useHits, useSearchBox } from "react-instantsearch"

import { PRODUCT_INDEX_NAME, searchClient } from "@lib/search-client"
import LocalizedClientLink from "@modules/common/components/localized-client-link"

type Hit = { objectID: string; title: string | null; handle: string | null; thumbnail: string | null }

const Suggestions = ({
  value,
  open,
  onNavigate,
}: {
  value: string
  open: boolean
  onNavigate: () => void
}) => {
  const timer = useRef<number | undefined>(undefined)
  const queryHook = useCallback((q: string, search: (v: string) => void) => {
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => search(q), 200)
  }, [])
  const { refine } = useSearchBox({ queryHook })
  const { items } = useHits<Hit>()

  useEffect(() => {
    refine(value)
  }, [value, refine])

  if (!open || !value.trim() || !items.length) {
    return null
  }

  return (
    <ul
      className="absolute left-0 right-0 top-full mt-px bg-white text-zl-ink border border-zl-line shadow-drawer z-50 max-h-[70vh] overflow-y-auto divide-y divide-zl-line"
      data-testid="search-results"
    >
      {items.slice(0, 8).map((hit) =>
        hit.handle ? (
          <li key={hit.objectID}>
            <LocalizedClientLink
              href={`/products/${hit.handle}`}
              onClick={onNavigate}
              className="flex items-center gap-x-3 px-3 py-2 hover:bg-zl-surface text-sm"
            >
              <span className="relative w-9 h-9 shrink-0 bg-zl-surface">
                {hit.thumbnail && (
                  <Image src={hit.thumbnail} alt="" fill sizes="36px" className="object-cover" />
                )}
              </span>
              <span className="line-clamp-1 font-medium">{hit.title}</span>
            </LocalizedClientLink>
          </li>
        ) : null
      )}
    </ul>
  )
}

const HeaderSearch = () => {
  const router = useRouter()
  const { countryCode } = useParams() as { countryCode: string }
  const [value, setValue] = useState("")
  const [open, setOpen] = useState(false)
  const wrapper = useRef<HTMLFormElement>(null)

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!wrapper.current?.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", onClick)
    return () => document.removeEventListener("mousedown", onClick)
  }, [])

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    setOpen(false)
    const params = new URLSearchParams()
    if (value.trim()) {
      params.set(`${PRODUCT_INDEX_NAME}[query]`, value.trim())
    }
    router.push(`/${countryCode}/store${params.size ? `?${params.toString()}` : ""}`)
  }

  return (
    <form
      ref={wrapper}
      onSubmit={submit}
      role="search"
      className="relative flex flex-1 h-10 border-b border-zl-ink focus-within:border-b-2 focus-within:border-zl-red"
    >
      <button
        type="submit"
        aria-label="Buscar"
        className="h-full w-7 flex items-center justify-start text-zl-ink hover:text-zl-red"
        data-testid="nav-search-button"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
          <circle cx="10.5" cy="10.5" r="6.5" />
          <path d="M15.5 15.5 L21 21" strokeLinecap="square" />
        </svg>
      </button>
      <input
        type="search"
        value={value}
        onChange={(e) => {
          setValue(e.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        placeholder="BUSCAR PIEZA / REF."
        aria-label="Buscar productos"
        className="flex-1 min-w-0 h-full bg-transparent text-sm text-zl-ink outline-none placeholder:font-mono placeholder:text-xs placeholder:tracking-[0.06em] placeholder:text-zl-mute"
        data-testid="search-input"
      />
      <InstantSearch
        indexName={PRODUCT_INDEX_NAME}
        searchClient={searchClient as unknown as SearchClient}
        future={{ preserveSharedStateOnUnmount: true }}
      >
        <Configure hitsPerPage={8} />
        <Suggestions value={value} open={open} onNavigate={() => setOpen(false)} />
      </InstantSearch>
    </form>
  )
}

export default HeaderSearch
